import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  BlogPostStatus,
  type BlogCategory,
  type BlogPost,
} from '@nucleus-commerce/api-client/admin';

import { blogCategoriesQueryOptions } from '../hooks/use-blog-categories';

/** Fields GET /blog/posts sorts on; anything else is a 400. */
export const BLOG_POST_SORTS = ['publishedAt', 'updatedAt', 'title'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const BLOG_POST_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(BlogPostStatus) },
  { key: 'categoryId', type: 'async' },
  { key: 'tag', type: 'text' },
  { key: 'publishedAt', type: 'date-range' },
] as const;

const categoryOption = (category: BlogCategory): FilterOption => ({
  value: category.id,
  label: category.title,
});

export interface BlogPostFiltersOptions {
  /** The `blog` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
}

/** Filters of the posts table, in the order the filter menu lists them. */
export function blogPostFilters({
  t,
  queryClient,
  storeId,
}: BlogPostFiltersOptions): Array<FilterDef<BlogPost>> {
  // Categories are bounded (100 per store) and served whole, so the picker searches them here.
  const categories = async () =>
    (await queryClient.query(blogCategoriesQueryOptions(storeId))).map(categoryOption);

  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: valuesOf(BlogPostStatus).map(({ value }) => ({
        value,
        label: t(`status.${value}`),
      })),
    },
    {
      key: 'categoryId',
      label: t('filters.category'),
      type: 'async',
      pinned: true,
      aliases: ['category'],
      debounceMs: 0,
      loadOptions: async (query) => {
        const needle = query.trim().toLowerCase();
        const options = await categories();
        return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
      },
      resolveOptions: async (values) =>
        (await categories()).filter((o) => values.includes(o.value)),
    },
    { key: 'tag', label: t('filters.tag'), type: 'text' },
    {
      key: 'publishedAt',
      label: t('filters.publishedAt'),
      type: 'date-range',
      aliases: ['published'],
    },
  ];
}
